import { Delegate } from '../lib/delegate.ts';
import { AppStore, type StoreCallback } from '../lib/app-store.ts';
import { templateParse } from '../lib/template-parse.ts';
import { toCamelCase } from '../lib/utils.ts';
import { apiClient } from '../lib/api-client.ts';
import { enforcePerm } from '../lib/perm.ts';
import type { ApiClient } from '../types/api-client.ts';

// --- Tipi locali ---

interface ListenerEntry {
  element: Element | Document | Window;
  type: string;
  handler: EventListener;
  options?: boolean | AddEventListenerOptions;
}

interface BootstrapGlobal {
  Modal?: { getInstance(el: Element): { hide(): void } | null };
}

// --- AppComponent ---

export default class AppComponent extends HTMLElement {
  static componentName = '';
  static observedAttributes: string[] = [];

  static register(): void {
    if (!this.componentName) {
      console.warn('[AppComponent] componentName vuoto, registrazione saltata');
      return;
    }
    if (customElements.get(this.componentName)) return;
    customElements.define(this.componentName, this);
  }

  // --- Delegate (lazy) ---

  private _delegate: Delegate | null = null;

  protected get delegate(): Delegate {
    if (!this._delegate) {
      this._delegate = new Delegate(this);
    }
    return this._delegate;
  }
  // --- API client access ---

  protected get api(): ApiClient {
    return apiClient;
  }
  // --- Tracked event listeners ---

  private _listeners = new Map<symbol, ListenerEntry>();

  protected listen(
    element: Element | Document | Window,
    type: string,
    handler: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): symbol {
    const key = Symbol();
    element.addEventListener(type, handler, options);
    this._listeners.set(key, { element, type, handler: handler as EventListener, options });
    return key;
  }

  protected unlisten(key: symbol): boolean {
    const entry = this._listeners.get(key);
    if (!entry) return false;
    entry.element.removeEventListener(entry.type, entry.handler, entry.options);
    this._listeners.delete(key);
    return true;
  }

  // --- Custom event dispatch ---

  protected dispatch(name: string, detail?: unknown): CustomEvent {
    const event = new CustomEvent(name, { detail, bubbles: true, composed: true });
    this.dispatchEvent(event);
    return event;
  }

  protected dispatchGlobal(name: string, detail?: unknown): CustomEvent {
    const event = new CustomEvent(name, { detail, bubbles: true, composed: true });
    document.dispatchEvent(event);
    return event;
  }

  // --- Chiusura modale / notifica al contesto parent ---

  /** Iframe: postMessage al parent; altrimenti chiude la modale Bootstrap che contiene il componente ed emette `eventName` */
  protected notifyAndClose(eventName: string, detail: { type: string; id: number }): void {
    if (window !== window.parent) {
      window.parent.postMessage({ action: 'modal-close', refresh: true, detail }, window.location.origin);
      return;
    }
    const modalEl = this.closest('.modal');
    if (modalEl) {
      const bootstrap = (globalThis as { bootstrap?: BootstrapGlobal }).bootstrap;
      bootstrap?.Modal?.getInstance(modalEl)?.hide();
    }
    this.dispatch(eventName, detail);
  }

  // --- Typed attribute accessors ---

  protected attributeString(name: string, defaultValue = ''): string {
    return this.getAttribute(name) ?? defaultValue;
  }

  protected attributeNumber(name: string, defaultValue = 0): number {
    const raw = this.getAttribute(name);
    if (raw === null) return defaultValue;
    const n = Number(raw);
    return Number.isNaN(n) ? defaultValue : n;
  }

  protected attributeBoolean(name: string, defaultValue = false): boolean {
    const raw = this.getAttribute(name);
    if (raw === null) return defaultValue;
    return raw !== 'false';
  }

  // --- Store access ---

  protected get store(): AppStore {
    return AppStore.instance;
  }

  private _storeUnsubs: Array<() => void> = [];

  protected storeOn<T>(key: string, callback: StoreCallback<T>): () => void {
    const unsub = this.store.on(key, callback);
    this._storeUnsubs.push(unsub);
    return unsub;
  }

  // --- Component identity ---

  protected get componentName(): string {
    return (this.constructor as typeof AppComponent).componentName;
  }

  protected get templateId(): string {
    return this.componentName;
  }

  // --- Rendering ---

  protected buildRenderingData(): Record<string, unknown> {
    const data: Record<string, unknown> = {};

    for (const attr of this.attributes) {
      if (attr.name === 'data-options') continue;
      data[toCamelCase(attr.name)] = attr.value;
    }

    const optionsRaw = this.getAttribute('data-options');
    if (optionsRaw) {
      try {
        const options = JSON.parse(optionsRaw) as Record<string, unknown>;
        Object.assign(data, options);
      } catch {
        console.warn(`[${this.componentName}] data-options non e' JSON valido`);
      }
    }

    return data;
  }

  protected render(): void {
    const savedSlots = this.collectSlots();
    const html = templateParse(this.templateId, this.buildRenderingData());
    if (html !== null) {
      this.innerHTML = html;
      this.distributeSlots(savedSlots);
      enforcePerm(this);
      this.afterRender();
    } else {
      this.onRenderError();
    }
  }

  private collectSlots(): Map<string, DocumentFragment> {
    const slots = new Map<string, DocumentFragment>();
    for (const tmpl of this.querySelectorAll(':scope > template[data-slot]')) {
      const el = tmpl as HTMLTemplateElement;
      slots.set(el.dataset.slot!, el.content.cloneNode(true) as DocumentFragment);
    }
    return slots;
  }

  private distributeSlots(slots: Map<string, DocumentFragment>): void {
    for (const [name, fragment] of slots) {
      const target = this.querySelector(`[data-slot="${name}"]`);
      if (target) target.appendChild(fragment);
    }
  }

  protected afterRender(): void {}

  protected onRenderError(): void {
    this.innerHTML =
      '<div class="alert alert-danger" role="alert">' +
      `Errore rendering componente <strong>${this.componentName}</strong>` +
      '</div>';
  }

  // --- Lifecycle ---

  connectedCallback(): void {
    this.onConnected();
    this.render();
  }

  disconnectedCallback(): void {
    this.onDisconnected();
    // 1. Store subscriptions
    for (const unsub of this._storeUnsubs) unsub();
    this._storeUnsubs = [];
    // 2. Tracked listeners
    for (const [, entry] of this._listeners) {
      entry.element.removeEventListener(entry.type, entry.handler, entry.options);
    }
    this._listeners.clear();
    // 3. Delegate
    if (this._delegate) {
      this._delegate.destroy();
      this._delegate = null;
    }
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    this.onAttributeChanged(name, oldValue, newValue);
  }

  // --- Lifecycle hooks ---

  protected onConnected(): void {}
  protected onDisconnected(): void {}

  protected onAttributeChanged(
    _name: string,
    _oldValue: string | null,
    _newValue: string | null,
  ): void {}

  // --- DOM shortcuts ---

  protected $(selector: string): HTMLElement | null {
    return this.querySelector(selector);
  }

  protected $$(selector: string): HTMLElement[] {
    return Array.from(this.querySelectorAll(selector));
  }
}
